import React, { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import createNumberMask from 'text-mask-addons/dist/createNumberMask';
import { ToolBar } from '@1c/ui';
import { useNavigate } from 'src/utils/useNavigate';
import { FinalField, FinalForm } from 'src/components/FinalForm';
import LinkButton from 'src/components/LinkButton';
import Button from 'src/components/Button';
import InputField from 'src/components/InputField';
import Text from 'src/components/Text';
import {
  postTwoFactorEmailAuthorizationConfirmAPI,
  postTwoFactorEmailAuthorizationStartAPI,
} from 'src/serverApi/authorization';
import routes from 'src/constants/routes';
import styles from './styles.module.scss';

const getTextTime = (timeSeconds: number = 0) => {
  return timeSeconds > 60 ? `${Math.floor(timeSeconds / 60)} мин.` : `${timeSeconds} сек.`;
};

type Props = {
  authorizationInfo?: {
    userId: string;
    confirmedEmailMask: string;
  };
  onSubmit: () => void;
};

const EmailRecoveryForm: React.FC<Props> = ({ authorizationInfo, onSubmit }) => {
  const navigate = useNavigate();

  const inputMask = createNumberMask({
    integerLimit: 6,
    prefix: '',
    includeThousandsSeparator: false,
    allowLeadingZeroes: true,
  });

  const [isLoading, setIsLoading] = useState(false);
  const [confirmTimeout, setConfirmTimeout] = useState(0);
  const [attemptsRemained, setAttemptsRemained] = useState(0);

  useEffect(() => {
    twoFactorEmailAuthorizationStart();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const startConfirmTimer = (confirmTimeout: number) => {
    setConfirmTimeout(confirmTimeout);

    const interval = setInterval(() => {
      setConfirmTimeout(prev => {
        if (prev === 0) {
          clearInterval(interval);
          return prev;
        }

        return prev - 1;
      });
    }, 1 * 1000);
  };

  const twoFactorEmailAuthorizationStart = async () => {
    try {
      setIsLoading(true);

      const sessionConfirmationState = await postTwoFactorEmailAuthorizationStartAPI();

      setAttemptsRemained(sessionConfirmationState?.attemptsRemained);
      startConfirmTimer(sessionConfirmationState?.timeoutSeconds);
    } catch (error) {
      toast.error('Произошла ошибка. Попробуйте позже.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleRepeat = () => {
    twoFactorEmailAuthorizationStart();
  };

  const confirmAuthorization = async (data: any) => {
    try {
      setIsLoading(true);
      const confirmData = await postTwoFactorEmailAuthorizationConfirmAPI(data);

      if (confirmData?.status === 'SUCCESS') {
        onSubmit();
        return;
      }

      // при ошибке кода подтверждения бэкенд присылает 200 ответ и в данных объект с status = 'IN_PROGRESS'
      // также уменьшается счетчик attemptsRemained
      // поэтому эта ошибка обрабатывается отдельно, не в блоке catch
      if (confirmData?.status === 'IN_PROGRESS') {
        if (confirmData?.attemptsRemained < attemptsRemained) {
          setAttemptsRemained(confirmData?.attemptsRemained);
          toast.error(
            `Некорректный код подтверждения. Осталось попыток: ${confirmData?.attemptsRemained}`
          );
        }
      }
    } catch (error) {
      const { type, params } = error as any;
      const messages: { [key: string]: string } = {
        ['CONCURRENT_EXECUTION']:
          'В другом сеансе сейчас работают с этим номером телефона. Необходимо подождать.',
        ['MAX_ATTEMPTS_EXCEEDED']: `Превышено количество попыток ввода кода. Повторить ввод можно через ${getTextTime(
          params?.timeoutSeconds
        )}`,
        ['NOT_FOUND']: 'Не определен номер телефона, для которого отправлено подтверждение.',
        ['PHONE_PROCESSING_EXCEPTION ']: 'неизвестная ошибка у оператора SMS.',
      };
      toast.error(messages[type] || 'Произошла ошибка. Попробуйте позже.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleBack = () => {
    navigate(routes.authorization);
  };

  const handleSubmit = (values: any) => {
    confirmAuthorization(values?.code);
  };

  return (
    <>
      <Text type="p" muted>
        На почту {authorizationInfo?.confirmedEmailMask} было отправлено письмо с кодом.
      </Text>
      <FinalForm
        onSubmit={handleSubmit}
        submitting={isLoading}
        pristine
        initialValues={{ code: undefined }}
      >
        {({ submitting, pristine }: { submitting: any; pristine: any }) => {
          return (
            <div className={styles['form']}>
              <FinalField name="code">
                {({ onChange }: { onChange: any }) => (
                  <InputField label="Введите код" onChange={onChange} mask={inputMask} />
                )}
              </FinalField>
              <div className={styles['resend-wrapper']}>
                <Text type="span" small muted>
                  Не пришло письмо? Проверьте папку СПАМ
                </Text>
                <br />
                <LinkButton
                  disabled={confirmTimeout > 0}
                  onClick={handleRepeat}
                  className={styles['link']}
                >
                  Отправить письмо повторно
                </LinkButton>
                {confirmTimeout > 0 && (
                  <Text type="span" small muted>
                    {' '}
                    (через {confirmTimeout})
                  </Text>
                )}
              </div>
              <ToolBar align="center" className={styles['buttons']}>
                <Button variant="text" disabled={submitting} onClick={handleBack}>
                  Назад
                </Button>
                <Button disabled={submitting || pristine} type="submit">
                  Продолжить
                </Button>
              </ToolBar>
            </div>
          );
        }}
      </FinalForm>
    </>
  );
};

export default EmailRecoveryForm;
